import { useState, useEffect } from 'react'
import { useClipSources, useClipFeeds, useClipStats, useClipLog } from '@/hooks/useClipagem'
import type { RssFeed } from '@/hooks/useClipagem'
import RichContentEditor from './RichContentEditor'

// Importações corretas do ReactQuill e seus estilos globais posicionadas no topo do arquivo

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    draft: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    review: 'bg-blue-50 text-blue-700 border-blue-200',
    published: 'bg-green-50 text-green-700 border-green-200',
    archived: 'bg-gray-50 text-gray-700 border-gray-200',
  }
  const labels: Record<string, string> = {
    draft: 'Rascunho',
    review: 'Revisão',
    published: 'Publicado',
    archived: 'Arquivado',
  }
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs border ${colors[status] || colors.draft}`}>
      {labels[status] || status}
    </span>
  )
}

function LanguageBadge({ language }: { language: string }) {
  const labels: Record<string, string> = {
    pt: 'Português',
    en: 'Inglês',
    es: 'Espanhol',
    fr: 'Francês',
    it: 'Italiano',
  }
  return (
    <span className="text-xs text-alvinegro-muted">{labels[language] || language}</span>
  )
}

export default function ClipManager() {
  const { sources, loading: sourcesLoading } = useClipSources()
  const { feeds, loading, totalCount, fetchFeeds, fetchFeedIds, updateFeedStatus, deleteFeed, publishToNews } = useClipFeeds()
  const { stats, loading: statsLoading, refetch } = useClipStats()
  const { logs, loading: logsLoading, refetch: refetchLogs } = useClipLog()

  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [sourceFilter, setSourceFilter] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const [publishConfirm, setPublishConfirm] = useState<RssFeed | null>(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [showEditor, setShowEditor] = useState<RssFeed | null>(null)
  const [fetchingRSS, setFetchingRSS] = useState(false)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [bulkStatus, setBulkStatus] = useState<RssFeed['status']>('review')
  const [publishOptions, setPublishOptions] = useState({
    is_featured: false,
    is_headline: false,
    is_breaking: false,
  })
  const perPage = 10

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  useEffect(() => {
    fetchFeeds(page, perPage, search, statusFilter, sourceFilter, startDate, endDate)
    setSelectedIds([])
  }, [page, search, statusFilter, sourceFilter, startDate, endDate])

  const handleFetchRSS = async () => {
    setFetchingRSS(true)
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/fetch-rss-feeds`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            startDate,
            endDate
          })
        }
      )
      const responseText = await response.text()
      const result = responseText ? JSON.parse(responseText) : {}

      if (!response.ok) {
        throw new Error(result.error || result.message || `Erro ${response.status} ao buscar feeds`)
      }

      if (result.success) {
        showToast(`${result.totalImported} notícias importadas com sucesso!`)
        refetch()
        refetchLogs()
        fetchFeeds(page, perPage, search, statusFilter, sourceFilter, startDate, endDate)
      } else {
        showToast(result.error || result.message || 'Erro ao buscar feeds', 'error')
      }
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Erro ao buscar feeds', 'error')
    }
    setFetchingRSS(false)
  }

  const openPublishConfirm = (feed: RssFeed) => {
    setPublishOptions({ is_featured: false, is_headline: false, is_breaking: false })
    setPublishConfirm(feed)
  }

  const handlePublish = async (feed: RssFeed) => {
    setActionLoading(true)
    const result = await publishToNews(feed, publishOptions)
    if (result) {
      showToast('Notícia publicada com sucesso!')
      setPublishConfirm(null)
      setPublishOptions({ is_featured: false, is_headline: false, is_breaking: false })
      fetchFeeds(page, perPage, search, statusFilter, sourceFilter, startDate, endDate)
      refetch()
    } else {
      showToast('Erro ao publicar notícia', 'error')
    }
    setActionLoading(false)
  }

  const handleDelete = async (id: string) => {
    setActionLoading(true)
    const success = await deleteFeed(id)
    if (success) {
      showToast('Notícia excluída')
      setDeleteConfirm(null)
      fetchFeeds(page, perPage, search, statusFilter, sourceFilter, startDate, endDate)
      refetch()
    } else {
      showToast('Erro ao excluir', 'error')
    }
    setActionLoading(false)
  }

  const handleArchive = async (id: string) => {
    setActionLoading(true)
    const result = await updateFeedStatus(id, 'archived')
    if (result) {
      showToast('Notícia arquivada')
      fetchFeeds(page, perPage, search, statusFilter, sourceFilter, startDate, endDate)
      refetch()
    } else {
      showToast('Erro ao arquivar', 'error')
    }
    setActionLoading(false)
  }

  const handleReview = async (id: string) => {
    setActionLoading(true)
    const result = await updateFeedStatus(id, 'review')
    if (result) {
      showToast('Notícia em revisão')
      fetchFeeds(page, perPage, search, statusFilter, sourceFilter, startDate, endDate)
    } else {
      showToast('Erro ao atualizar', 'error')
    }
    setActionLoading(false)
  }

  const totalPages = Math.ceil(totalCount / perPage)
  const selectedFeedsCount = selectedIds.length
  const allCurrentResultsSelected = totalCount > 0 && selectedIds.length === totalCount

  const refreshFeeds = () => {
    fetchFeeds(page, perPage, search, statusFilter, sourceFilter, startDate, endDate)
    refetch()
  }

  const toggleSelectAllResults = async () => {
    if (allCurrentResultsSelected) {
      setSelectedIds([])
      return
    }
    const allIds = await fetchFeedIds(search, statusFilter, sourceFilter, startDate, endDate)
    setSelectedIds(allIds.map((feed) => feed.id))
  }

  const toggleSelectFeed = (id: string) => {
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    )
  }

  const selectUnpublished = async () => {
    const allIds = await fetchFeedIds(search, '', sourceFilter, startDate, endDate)
    setSelectedIds(allIds.filter((feed) => feed.status !== 'published').map((feed) => feed.id))
  }

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return
    setActionLoading(true)
    const results = await Promise.all(selectedIds.map((id) => deleteFeed(id)))
    const deletedCount = results.filter(Boolean).length
    showToast(`${deletedCount} notícia(s) excluída(s)`)
    setSelectedIds([])
    setActionLoading(false)
    refreshFeeds()
  }

  const handleBulkStatusChange = async () => {
    if (selectedIds.length === 0) return
    setActionLoading(true)
    const results = await Promise.all(selectedIds.map((id) => updateFeedStatus(id, bulkStatus)))
    const updatedCount = results.filter(Boolean).length
    showToast(`${updatedCount} notícia(s) atualizada(s)`)
    setSelectedIds([])
    setActionLoading(false)
    refreshFeeds()
  }

  return (
    <div>
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Hoje', value: stats.totalToday, icon: 'ri-calendar-line', color: 'bg-alvinegro-black' },
          { label: 'Semana', value: stats.totalWeek, icon: 'ri-calendar-event-line', color: 'bg-alvinegro-dark' },
          { label: 'Pendentes', value: stats.pending, icon: 'ri-draft-line', color: 'bg-yellow-500' },
          { label: 'Publicadas', value: stats.published, icon: 'ri-check-double-line', color: 'bg-green-500' },
        ].map((card) => (
          <div key={card.label} className="bg-white rounded-xl border border-alvinegro-border p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full ${card.color} flex items-center justify-center shrink-0`}>
                <i className={`${card.icon} text-white text-base`}></i>
              </div>
              <div>
                <p className="text-2xl font-bold text-alvinegro-black">{statsLoading ? '-' : card.value}</p>
                <p className="text-xs text-alvinegro-muted">{card.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Sources Status */}
      <div className="bg-white rounded-xl border border-alvinegro-border p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-sm text-alvinegro-black">Fontes RSS</h3>
          <span className="text-xs text-alvinegro-muted">{stats.totalSources} fontes cadastradas</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {sourcesLoading ? (
            <div className="h-6 w-32 bg-alvinegro-light rounded animate-pulse" />
          ) : (
            sources.map((source) => (
              <span
                key={source.id}
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                  source.status === 'active'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-gray-50 text-gray-500 border border-gray-200'
                }`}
              >
                <i className={`ri-${source.status === 'active' ? 'checkbox-circle' : 'close-circle'}-line`}></i>
                {source.name}
              </span>
            ))
          )}
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-1 w-full flex-wrap">
          <div className="relative flex-1 max-w-sm">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-alvinegro-muted"></i>
            <input
              type="text"
              placeholder="Buscar notícias..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1) }}
              className="w-full pl-9 pr-4 py-2 rounded-full border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
            className="px-4 py-2 rounded-full border border-alvinegro-border text-sm bg-white focus:outline-none focus:border-alvinegro-black"
          >
            <option value="">Todos status</option>
            <option value="draft">Rascunho</option>
            <option value="review">Revisão</option>
            <option value="published">Publicado</option>
            <option value="archived">Arquivado</option>
          </select>
          <select
            value={sourceFilter}
            onChange={(e) => { setSourceFilter(e.target.value); setPage(1) }}
            className="px-4 py-2 rounded-full border border-alvinegro-border text-sm bg-white focus:outline-none focus:border-alvinegro-black"
          >
            <option value="">Todas fontes</option>
            {sources.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          <input
            type="date"
            value={startDate}
            onChange={(e) => { setStartDate(e.target.value); setPage(1) }}
            className="px-4 py-2 rounded-full border border-alvinegro-border text-sm bg-white focus:outline-none focus:border-alvinegro-black"
          />
          <input
            type="date"
            value={endDate}
            onChange={(e) => { setEndDate(e.target.value); setPage(1) }}
            className="px-4 py-2 rounded-full border border-alvinegro-border text-sm bg-white focus:outline-none focus:border-alvinegro-black"
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleFetchRSS}
            disabled={fetchingRSS}
            className="flex items-center gap-2 bg-alvinegro-black text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-alvinegro-dark transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            <i className={`ri-refresh-line ${fetchingRSS ? 'animate-spin' : ''}`}></i>
            {fetchingRSS ? 'Buscando...' : 'Buscar RSS'}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-alvinegro-border p-4 mb-6">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={toggleSelectAllResults}
              disabled={feeds.length === 0 || actionLoading}
              className="px-4 py-2 rounded-full border border-alvinegro-border text-sm font-medium text-alvinegro-dark hover:bg-alvinegro-light transition-colors cursor-pointer disabled:opacity-50"
            >
              {allCurrentResultsSelected ? 'Limpar seleção' : 'Selecionar todos'}
            </button>

            <button
              type="button"
              onClick={selectUnpublished}
              disabled={feeds.length === 0 || actionLoading}
              className="px-4 py-2 rounded-full border border-alvinegro-border text-sm font-medium text-alvinegro-dark hover:bg-alvinegro-light transition-colors cursor-pointer disabled:opacity-50"
            >
              Selecionar não publicados
            </button>

            <span className="text-sm text-alvinegro-muted">
              {selectedFeedsCount} selecionado(s)
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <select
              value={bulkStatus}
              onChange={(e) => setBulkStatus(e.target.value as RssFeed['status'])}
              disabled={selectedIds.length === 0 || actionLoading}
              className="px-4 py-2 rounded-full border border-alvinegro-border text-sm bg-white focus:outline-none focus:border-alvinegro-black disabled:opacity-50"
            >
              <option value="draft">Rascunho</option>
              <option value="review">Revisão</option>
              <option value="published">Publicado</option>
              <option value="archived">Arquivado</option>
            </select>

            <button
              type="button"
              onClick={handleBulkStatusChange}
              disabled={selectedIds.length === 0 || actionLoading}
              className="px-4 py-2 rounded-full bg-alvinegro-black text-white text-sm font-medium hover:bg-alvinegro-dark transition-colors cursor-pointer disabled:opacity-50"
            >
              Alterar status
            </button>

            <button
              type="button"
              onClick={handleBulkDelete}
              disabled={selectedIds.length === 0 || actionLoading}
              className="px-4 py-2 rounded-full bg-red-500 text-white text-sm font-medium hover:bg-red-700 transition-colors cursor-pointer disabled:opacity-50"
            >
              Deletar selecionados
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-alvinegro-border overflow-hidden mb-6">
        {loading ? (
          <div className="p-8 space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 animate-pulse">
                <div className="w-16 h-12 rounded bg-alvinegro-light" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-alvinegro-light rounded w-1/4" />
                  <div className="h-3 bg-alvinegro-light rounded w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-alvinegro-border bg-alvinegro-light">
                    <th className="text-left px-4 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={allCurrentResultsSelected}
                        onChange={toggleSelectAllResults}
                        className="w-4 h-4 rounded border-alvinegro-border"
                        aria-label="Selecionar todos"
                      />
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider">Notícia</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider hidden md:table-cell">Fonte</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider hidden sm:table-cell">Idioma</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider hidden sm:table-cell">Data</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {feeds.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center text-alvinegro-muted">
                        <i className="ri-article-line text-4xl mb-3 block"></i>
                        <p className="text-sm">Nenhuma notícia importada</p>
                        <p className="text-xs mt-1">Clique em "Buscar RSS" para importar notícias</p>
                      </td>
                    </tr>
                  ) : (
                    feeds.map((feed) => (
                      <tr key={feed.id} className="border-b border-alvinegro-border hover:bg-alvinegro-light/50 transition-colors">
                        <td className="px-4 py-3 align-middle">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(feed.id)}
                            onChange={() => toggleSelectFeed(feed.id)}
                            className="w-4 h-4 rounded border-alvinegro-border"
                            aria-label={`Selecionar ${feed.title}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {feed.image_url && (
                              <div className="w-16 h-12 rounded-lg bg-alvinegro-light overflow-hidden shrink-0 hidden sm:block">
                                <img src={feed.image_url} alt="" className="w-full h-full object-cover" />
                              </div>
                            )}
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-alvinegro-black truncate max-w-[200px] md:max-w-[300px]">{feed.title}</p>
                              <p className="text-xs text-alvinegro-muted truncate max-w-[200px] md:max-w-[300px]">{feed.subtitle || feed.summary || ''}</p>
                              {feed.category && (
                                <span className="text-xs text-alvinegro-gold mt-1 inline-block">{feed.category}</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <span className="text-xs text-alvinegro-muted">{feed.source_name || '-'}</span>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <LanguageBadge language={feed.language} />
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={feed.status} />
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <span className="text-xs text-alvinegro-muted">
                            {feed.fetched_at ? new Date(feed.fetched_at).toLocaleDateString('pt-BR') : '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => setShowEditor(feed)}
                              className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-alvinegro-light text-alvinegro-dark transition-colors cursor-pointer"
                              title="Editar"
                            >
                              <i className="ri-pencil-line text-sm"></i>
                            </button>
                            {feed.status === 'draft' && (
                              <>
                                <button
                                  onClick={() => handleReview(feed.id)}
                                  disabled={actionLoading}
                                  className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-blue-50 text-blue-600 transition-colors cursor-pointer"
                                  title="Enviar para Revisão"
                                >
                                  <i className="ri-eye-line text-sm"></i>
                                </button>
                                <button
                                  onClick={() => openPublishConfirm(feed)}
                                  disabled={actionLoading}
                                  className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-green-50 text-green-600 transition-colors cursor-pointer"
                                  title="Publicar"
                                >
                                  <i className="ri-check-line text-sm"></i>
                                </button>
                              </>
                            )}
                            {feed.status === 'review' && (
                              <button
                              onClick={() => openPublishConfirm(feed)}
                                disabled={actionLoading}
                                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-green-50 text-green-600 transition-colors cursor-pointer"
                                title="Publicar"
                              >
                                <i className="ri-check-line text-sm"></i>
                              </button>
                            )}
                            <button
                              onClick={() => handleArchive(feed.id)}
                              disabled={actionLoading}
                              className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-50 text-gray-600 transition-colors cursor-pointer"
                              title="Arquivar"
                            >
                              <i className="ri-archive-line text-sm"></i>
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(feed.id)}
                              disabled={actionLoading}
                              className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-red-50 text-red-600 transition-colors cursor-pointer"
                              title="Excluir"
                            >
                              <i className="ri-delete-bin-line text-sm"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-alvinegro-border">
                <p className="text-xs text-alvinegro-muted">
                  Mostrando {Math.min((page - 1) * perPage + 1, totalCount)}-{Math.min(page * perPage, totalCount)} de {totalCount}
                </p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-alvinegro-light transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <i className="ri-arrow-left-s-line text-sm"></i>
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = i + Math.max(1, Math.min(page - 2, totalPages - 4))
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`w-8 h-8 rounded-full text-sm font-medium transition-colors cursor-pointer ${
                          pageNum === page ? 'bg-alvinegro-black text-white' : 'hover:bg-alvinegro-light text-alvinegro-dark'
                        }`}
                      >
                        {pageNum}
                      </button>
                    )
                  })}
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-alvinegro-light transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <i className="ri-arrow-right-s-line text-sm"></i>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Recent Logs */}
      <div className="bg-white rounded-xl border border-alvinegro-border p-6">
        <h3 className="font-semibold text-sm text-alvinegro-black mb-4">Últimas Atividades</h3>
        {logsLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-4 bg-alvinegro-light rounded animate-pulse" />
            ))}
          </div>
        ) : logs.length === 0 ? (
          <p className="text-sm text-alvinegro-muted">Nenhuma atividade registrada</p>
        ) : (
          <div className="space-y-2">
            {logs.slice(0, 5).map((log) => (
              <div key={log.id} className="flex items-center gap-3 py-2 border-b border-alvinegro-border last:border-0">
                <div className="w-2 h-2 rounded-full bg-alvinegro-black" />
                <div className="flex-1">
                  <p className="text-sm text-alvinegro-dark">
                    {log.action === 'fetch' && 'Busca de RSS executada'}
                    {log.action === 'import' && 'Notícias importadas'}
                    {log.action === 'publish' && 'Notícia publicada'}
                    {log.action === 'archive' && 'Notícia arquivada'}
                    {!['fetch', 'import', 'publish', 'archive'].includes(log.action) && log.action}
                  </p>
                  <p className="text-xs text-alvinegro-muted">
                    {log.items_count} item(s) • {new Date(log.created_at).toLocaleString('pt-BR')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Publish Confirm */}
      {publishConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
                <i className="ri-check-line text-green-500 text-lg"></i>
              </div>
              <h3 className="font-semibold text-alvinegro-black">Publicar Notícia</h3>
            </div>
            <p className="text-sm text-alvinegro-muted mb-2">Deseja publicar esta notícia importada no portal?</p>
            <p className="text-sm font-medium text-alvinegro-black mb-4">{publishConfirm.title}</p>

            <div className="space-y-3 rounded-xl border border-alvinegro-border bg-alvinegro-light/50 p-4 mb-6">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={publishOptions.is_featured}
                  onChange={(event) => setPublishOptions({ ...publishOptions, is_featured: event.target.checked })}
                  className="w-4 h-4 rounded border-alvinegro-border"
                />
                Destaque
              </label>

              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={publishOptions.is_headline}
                  onChange={(event) => setPublishOptions({ ...publishOptions, is_headline: event.target.checked })}
                  className="w-4 h-4 rounded border-alvinegro-border"
                />
                Manchete principal
              </label>

              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={publishOptions.is_breaking}
                  onChange={(event) => setPublishOptions({ ...publishOptions, is_breaking: event.target.checked })}
                  className="w-4 h-4 rounded border-alvinegro-border"
                />
                Breaking News
              </label>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => setPublishConfirm(null)} className="flex-1 py-2.5 rounded-full border border-alvinegro-border text-sm font-medium text-alvinegro-dark hover:bg-alvinegro-light transition-colors cursor-pointer">Cancelar</button>
              <button onClick={() => handlePublish(publishConfirm)} disabled={actionLoading} className="flex-1 py-2.5 rounded-full bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors cursor-pointer disabled:opacity-50">{actionLoading ? 'Publicando...' : 'Publicar'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                <i className="ri-delete-bin-line text-red-600 text-lg"></i>
              </div>
              <h3 className="font-semibold text-alvinegro-black">Excluir notícia</h3>
            </div>
            <p className="text-sm text-alvinegro-muted mb-6">Tem certeza que deseja excluir esta notícia permanentemente?</p>
            <div className="flex items-center gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 rounded-full border border-alvinegro-border text-sm font-medium text-alvinegro-dark hover:bg-alvinegro-light transition-colors cursor-pointer">Cancelar</button>
              <button onClick={() => handleDelete(deleteConfirm)} disabled={actionLoading} className="flex-1 py-2.5 rounded-full bg-red-500 text-white text-sm font-medium hover:bg-red-700 transition-colors cursor-pointer disabled:opacity-50">{actionLoading ? 'Excluindo...' : 'Excluir'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Clip Editor */}
      {showEditor && (
        <ClipEditor feed={showEditor} onClose={() => setShowEditor(null)} onSave={() => {
          setShowEditor(null)
          fetchFeeds(page, perPage, search, statusFilter, sourceFilter, startDate, endDate)
          refetch()
        }} />
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50">
          <div className={`rounded-xl px-5 py-3 shadow-lg border text-sm font-medium flex items-center gap-2 ${toast.type === 'success' ? 'bg-white border-green-200 text-green-700' : 'bg-white border-red-200 text-red-600'}`}>
            <i className={`${toast.type === 'success' ? 'ri-check-double-line' : 'ri-error-warning-line'}`}></i>
            {toast.message}
          </div>
        </div>
      )}
    </div>
  )
}

/* Clip Editor Modal */
interface ClipEditorProps {
  feed: RssFeed
  onClose: () => void
  onSave: () => void
}

function ClipEditor({ feed, onClose, onSave }: ClipEditorProps) {
  const { updateFeed } = useClipFeeds()
  
  // Utiliza o hook principal useState de forma unificada e limpa
  const [form, setForm] = useState({
    title: feed.title || '',
    subtitle: feed.subtitle || '',
    content: feed.content || feed.summary || '',
    image_url: feed.image_url || '',
    category: feed.category || 'Geral',
    tags: feed.tags?.join(', ') || '',
    source: feed.source_name || '',
    source_url: feed.original_url || '',
    meta_description: (feed.summary || '').substring(0, 160),
    slug: feed.title?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'noticia',
  })
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const categories = ['Geral', 'Futebol', 'Internacional', 'Brasileirão', 'Copa do Brasil', 'Sul-Americana', 'Mercado da Bola', 'Institucional', 'Entrevista', 'Categorias de Base']

  const handleSave = async () => {
    setSaving(true)
    const result = await updateFeed(feed.id, {
      title: form.title,
      subtitle: form.subtitle,
      content: form.content,
      image_url: form.image_url,
      category: form.category,
      tags: form.tags.split(',').map((t: string) => t.trim()).filter(Boolean),
      source_name: form.source.trim() || null,
      original_url: form.source_url.trim() || null,
    })
    if (result) {
      setToast({ message: 'Rascunho atualizado!', type: 'success' })
      setTimeout(() => { setToast(null); onSave() }, 1000)
    } else {
      setToast({ message: 'Erro ao salvar', type: 'error' })
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-2xl p-6 md:p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-serif font-bold text-xl text-alvinegro-black">Editar Rascunho</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-alvinegro-light flex items-center justify-center cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-alvinegro-dark mb-1">Título</label>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-alvinegro-dark mb-1">Subtítulo</label>
            <input
              value={form.subtitle}
              onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-alvinegro-dark mb-1">Conteúdo</label>

            <RichContentEditor
              value={form.content}
              onChange={(content) => setForm({ ...form, content })}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-alvinegro-dark mb-1">Imagem URL</label>
              <input
                value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-alvinegro-dark mb-1">Categoria</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black bg-white"
              >
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-alvinegro-dark mb-1">Tags (separadas por vírgula)</label>
            <input
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-alvinegro-dark mb-1">Fonte</label>
              <input
                value={form.source}
                onChange={(e) => setForm({ ...form, source: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-alvinegro-dark mb-1">Link Original</label>
              <input
                value={form.source_url}
                onChange={(e) => setForm({ ...form, source_url: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-alvinegro-dark mb-1">Meta Description</label>
            <input
              value={form.meta_description}
              onChange={(e) => setForm({ ...form, meta_description: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-alvinegro-dark mb-1">Slug</label>
            <input
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black"
            />
          </div>
          <div className="flex items-center gap-3 pt-4">
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-alvinegro-black text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-alvinegro-dark transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {saving ? 'Salvando...' : 'Salvar Rascunho'}
            </button>
            <button
              onClick={onClose}
              className="px-6 py-2.5 rounded-full border border-alvinegro-border text-sm font-medium text-alvinegro-dark hover:bg-alvinegro-light transition-colors cursor-pointer"
            >
              Cancelar
            </button>
          </div>
        </div>

        {/* Toast interno do modal */}
        {toast && (
          <div className="mt-4">
            <div className={`rounded-xl px-4 py-2 border text-sm font-medium flex items-center gap-2 ${toast.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-600'}`}>
              <i className={`${toast.type === 'success' ? 'ri-check-double-line' : 'ri-error-warning-line'}`}></i>
              {toast.message}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
